/**
 * Type-safe wrappers каталога услуг v2 (epic #220, фаза 5).
 *
 * Контракт из согласованного proposal (architecture v2 2026-06-11, раздел 2):
 *
 *   Витрина (гость, авторизованный или через guest-token):
 *     GET  /v1/service-catalog?locale=ru            — категории → услуги → варианты
 *
 *   Конструктор (admin CRUD, RequireCapability tenant:admin):
 *     GET/POST   /v1/admin/service-catalog/categories
 *     PATCH      /v1/admin/service-catalog/categories/{id}    (вкл. is_active/sort_order)
 *     GET/POST   /v1/admin/service-catalog/items?category_id=
 *     PATCH      /v1/admin/service-catalog/items/{id}
 *     GET/POST   /v1/admin/service-catalog/variants?item_id=
 *     PATCH      /v1/admin/service-catalog/variants/{id}
 *     GET/POST   /v1/admin/service-catalog/workflow-templates
 *     PATCH      /v1/admin/service-catalog/workflow-templates/{id}
 *     GET/POST   /v1/admin/service-catalog/presets             (POST = снимок текущего каталога)
 *     POST       /v1/admin/service-catalog/presets/{id}/apply  (применить набор)
 *
 *   Заявка гостя:
 *     POST /v1/requests — v2-формат (category_id FK + nullable item_id/variant_id)
 *
 * Backend (фазы 1-3) идёт параллельно — до его выката consumers работают
 * против MSW-моков; при выходе API Contracts v1.5 сверить пути/поля здесь.
 */
import { customFetch } from './openapi-fetch.js';
import type {
  CatalogPreset,
  CatalogPresetSaveInput,
  CatalogPresetsListResponse,
  CatalogViewResponse,
  RequestTypesListResponse,
  ServiceCategoriesListResponse,
  ServiceCategory,
  ServiceCategoryCreateInput,
  ServiceCategoryUpdateInput,
  ServiceItem,
  ServiceItemCreateInput,
  ServiceItemUpdateInput,
  ServiceItemVariant,
  ServiceItemVariantCreateInput,
  ServiceItemVariantUpdateInput,
  ServiceItemVariantsListResponse,
  ServiceItemsListResponse,
  ServiceRequestCreateInput,
  ServiceRequestCreatedResponse,
  WorkflowTemplate,
  WorkflowTemplateCreateInput,
  WorkflowTemplateUpdateInput,
  WorkflowTemplatesListResponse,
} from '@hsc/types';

const ADMIN_BASE = '/admin/service-catalog';

/* ===================== Витрина ===================== */

/** GET /v1/service-catalog?locale= — локализованная витрина каталога. */
export async function getServiceCatalog(locale?: string): Promise<CatalogViewResponse> {
  return customFetch<CatalogViewResponse>(
    locale !== undefined && locale !== ''
      ? { url: '/service-catalog', method: 'GET', params: { locale } }
      : { url: '/service-catalog', method: 'GET' },
  );
}

/**
 * GET /v1/service-catalog/request-types?locale= — типы заявок (активные
 * категории с is_request_type=true, миграция 0068) для формы «Новая заявка».
 */
export async function getRequestTypes(locale?: string): Promise<RequestTypesListResponse> {
  return customFetch<RequestTypesListResponse>(
    locale !== undefined && locale !== ''
      ? { url: '/service-catalog/request-types', method: 'GET', params: { locale } }
      : { url: '/service-catalog/request-types', method: 'GET' },
  );
}

/** POST /v1/requests — заявка гостя на услугу/вариант (v2-формат, фаза 2). */
export async function createServiceRequest(
  input: ServiceRequestCreateInput,
): Promise<ServiceRequestCreatedResponse> {
  return customFetch<ServiceRequestCreatedResponse>({
    url: '/requests',
    method: 'POST',
    data: input,
  });
}

/* ===================== Категории (admin) ===================== */

export async function adminListServiceCategories(): Promise<ServiceCategoriesListResponse> {
  // Бэкенд отдаёт { categories, total }; нормализуем к контракту { items, count }.
  const raw = await customFetch<{
    categories?: ServiceCategory[];
    items?: ServiceCategory[];
    total?: number;
    count?: number;
  }>({
    url: `${ADMIN_BASE}/categories`,
    method: 'GET',
  });
  return { items: raw.categories ?? raw.items ?? [], count: raw.total ?? raw.count ?? 0 };
}

export async function adminCreateServiceCategory(
  input: ServiceCategoryCreateInput,
): Promise<ServiceCategory> {
  return customFetch<ServiceCategory>({
    url: `${ADMIN_BASE}/categories`,
    method: 'POST',
    data: input,
  });
}

/**
 * PATCH — частичное обновление полей. ВНИМАНИЕ: бэк-DTO категории НЕ содержит
 * is_active, поэтому активность через PATCH МОЛЧА игнорируется. Для архивации
 * используйте adminSetServiceCategoryActive (выделенный POST /activate|/deactivate).
 */
export async function adminUpdateServiceCategory(
  id: string,
  patch: ServiceCategoryUpdateInput,
): Promise<ServiceCategory> {
  return customFetch<ServiceCategory>({
    url: `${ADMIN_BASE}/categories/${encodeURIComponent(id)}`,
    method: 'PATCH',
    data: patch,
  });
}

/** POST /activate|/deactivate — единственный путь смены активности категории. */
export async function adminSetServiceCategoryActive(
  id: string,
  active: boolean,
): Promise<ServiceCategory> {
  return customFetch<ServiceCategory>({
    url: `${ADMIN_BASE}/categories/${encodeURIComponent(id)}/${active ? 'activate' : 'deactivate'}`,
    method: 'POST',
  });
}

/* ===================== Услуги (admin) ===================== */

export async function adminListServiceItems(
  categoryId?: string,
): Promise<ServiceItemsListResponse> {
  return customFetch<ServiceItemsListResponse>(
    categoryId !== undefined && categoryId !== ''
      ? { url: `${ADMIN_BASE}/items`, method: 'GET', params: { category_id: categoryId } }
      : { url: `${ADMIN_BASE}/items`, method: 'GET' },
  );
}

export async function adminCreateServiceItem(input: ServiceItemCreateInput): Promise<ServiceItem> {
  return customFetch<ServiceItem>({ url: `${ADMIN_BASE}/items`, method: 'POST', data: input });
}

export async function adminUpdateServiceItem(
  id: string,
  patch: ServiceItemUpdateInput,
): Promise<ServiceItem> {
  return customFetch<ServiceItem>({
    url: `${ADMIN_BASE}/items/${encodeURIComponent(id)}`,
    method: 'PATCH',
    data: patch,
  });
}

/** POST /activate|/deactivate — единственный путь смены активности услуги. */
export async function adminSetServiceItemActive(id: string, active: boolean): Promise<ServiceItem> {
  return customFetch<ServiceItem>({
    url: `${ADMIN_BASE}/items/${encodeURIComponent(id)}/${active ? 'activate' : 'deactivate'}`,
    method: 'POST',
  });
}

/* ===================== Варианты (admin) ===================== */

export async function adminListItemVariants(
  itemId: string,
): Promise<ServiceItemVariantsListResponse> {
  return customFetch<ServiceItemVariantsListResponse>({
    url: `${ADMIN_BASE}/variants`,
    method: 'GET',
    params: { item_id: itemId },
  });
}

export async function adminCreateItemVariant(
  input: ServiceItemVariantCreateInput,
): Promise<ServiceItemVariant> {
  return customFetch<ServiceItemVariant>({
    url: `${ADMIN_BASE}/variants`,
    method: 'POST',
    data: input,
  });
}

export async function adminUpdateItemVariant(
  id: string,
  patch: ServiceItemVariantUpdateInput,
): Promise<ServiceItemVariant> {
  return customFetch<ServiceItemVariant>({
    url: `${ADMIN_BASE}/variants/${encodeURIComponent(id)}`,
    method: 'PATCH',
    data: patch,
  });
}

/** POST /activate|/deactivate — единственный путь смены активности варианта. */
export async function adminSetItemVariantActive(
  id: string,
  active: boolean,
): Promise<ServiceItemVariant> {
  return customFetch<ServiceItemVariant>({
    url: `${ADMIN_BASE}/variants/${encodeURIComponent(id)}/${active ? 'activate' : 'deactivate'}`,
    method: 'POST',
  });
}

/* ===================== Шаблоны процессов (admin) ===================== */

export async function adminListWorkflowTemplates(): Promise<WorkflowTemplatesListResponse> {
  // Бэкенд отдаёт { workflow_templates, total }; нормализуем к контракту { items, count }.
  const raw = await customFetch<{
    workflow_templates?: WorkflowTemplate[];
    items?: WorkflowTemplate[];
    total?: number;
    count?: number;
  }>({
    url: `${ADMIN_BASE}/workflow-templates`,
    method: 'GET',
  });
  return { items: raw.workflow_templates ?? raw.items ?? [], count: raw.total ?? raw.count ?? 0 };
}

export async function adminCreateWorkflowTemplate(
  input: WorkflowTemplateCreateInput,
): Promise<WorkflowTemplate> {
  return customFetch<WorkflowTemplate>({
    url: `${ADMIN_BASE}/workflow-templates`,
    method: 'POST',
    data: input,
  });
}

export async function adminUpdateWorkflowTemplate(
  id: string,
  patch: WorkflowTemplateUpdateInput,
): Promise<WorkflowTemplate> {
  return customFetch<WorkflowTemplate>({
    url: `${ADMIN_BASE}/workflow-templates/${encodeURIComponent(id)}`,
    method: 'PATCH',
    data: patch,
  });
}

/** POST /activate|/deactivate — единственный путь смены активности шаблона. */
export async function adminSetWorkflowTemplateActive(
  id: string,
  active: boolean,
): Promise<WorkflowTemplate> {
  return customFetch<WorkflowTemplate>({
    url: `${ADMIN_BASE}/workflow-templates/${encodeURIComponent(id)}/${active ? 'activate' : 'deactivate'}`,
    method: 'POST',
  });
}

/* ===================== Пресеты (admin) ===================== */

export async function adminListCatalogPresets(): Promise<CatalogPresetsListResponse> {
  // Бэкенд отдаёт { presets, total }; нормализуем к контракту { items, count }.
  const raw = await customFetch<{
    presets?: CatalogPreset[];
    items?: CatalogPreset[];
    total?: number;
    count?: number;
  }>({
    url: `${ADMIN_BASE}/presets`,
    method: 'GET',
  });
  return { items: raw.presets ?? raw.items ?? [], count: raw.total ?? raw.count ?? 0 };
}

/** «Сохранить текущий каталог как набор» — POST снимка. */
export async function adminSaveCatalogPreset(
  input: CatalogPresetSaveInput,
): Promise<CatalogPreset> {
  return customFetch<CatalogPreset>({
    url: `${ADMIN_BASE}/presets`,
    method: 'POST',
    data: input,
  });
}

/** Применить набор к текущему каталогу тенанта. */
export async function adminApplyCatalogPreset(id: string): Promise<void> {
  await customFetch<void>({
    url: `${ADMIN_BASE}/presets/${encodeURIComponent(id)}/apply`,
    method: 'POST',
  });
}
